import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { FileSpreadsheet, FileText, ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Users, X, Search } from 'lucide-react';
import { Input } from './ui/input';
import api from '../lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Consolidated SO / Event Report dialog.
 *
 * Receives either a list of selected SO ids OR an `eventId` (auto-roll-up),
 * calls the backend aggregator, then renders:
 *   • "Size Grid" tab — Design × Colour × Size matrix (serial no, sortable,
 *      customer-filterable) with an inline customer drill-in.
 *   • "Customers" tab — design-wise list of customers who ordered + their qty.
 * Exports the full report to Excel + PDF.
 */
const ConsolidatedReportDialog = ({ open, onOpenChange, selectedIds, eventId, eventIds, eventName }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [exporting, setExporting] = useState(null);
  const [tab, setTab] = useState('grid');
  const [sortKey, setSortKey] = useState('design'); // 'design' | 'qty' | 'customers'
  const [sortDir, setSortDir] = useState('asc');
  const [custFilter, setCustFilter] = useState(''); // '' = all customers
  const [vendorFilter, setVendorFilter] = useState(''); // '' = all vendors
  const [searchTerm, setSearchTerm] = useState('');
  const [zoomImg, setZoomImg] = useState(null); // enlarged design image
  const [qtyMode, setQtyMode] = useState('all'); // 'all', 'pending', 'dispatched'

  const loadReport = async (mode) => {
    setLoading(true); setReport(null); setExpanded(new Set());
    try {
      const body = eventIds?.length ? { event_ids: eventIds } : eventId ? { event_id: eventId } : { order_ids: selectedIds };
      body.qty_mode = mode || qtyMode;
      const res = await api.post('/sales-orders/consolidated-report', body);
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to build report');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) { setReport(null); return; }
    if (!eventId && !eventIds?.length && !selectedIds?.length) { setReport(null); return; }
    setTab('grid'); setSortKey('design'); setSortDir('asc'); setCustFilter(''); setVendorFilter(''); setSearchTerm(''); setQtyMode('all');
    loadReport('all');
  }, [open, selectedIds, eventId, eventIds]);

  // Unique customers across all designs (for the filter dropdown)
  const allCustomers = useMemo(() => {
    if (!report) return [];
    const map = new Map();
    for (const d of report.designs) {
      for (const cb of d.customer_breakdown) {
        if (!map.has(cb.customer_id)) map.set(cb.customer_id, cb.customer_name);
      }
    }
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [report]);

  // Unique vendors across all designs (for the filter dropdown)
  const allVendors = useMemo(() => {
    if (!report) return [];
    const map = new Map();
    for (const d of report.designs) {
      if (d.vendor_id && d.vendor_name) {
        if (!map.has(d.vendor_id)) map.set(d.vendor_id, d.vendor_name);
      }
    }
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [report]);

  // Designs after search + customer filter + vendor filter + sort
  const visibleDesigns = useMemo(() => {
    if (!report) return [];
    let list = report.designs;
    // Search filter
    if (searchTerm) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter(d =>
        d.design_code?.toLowerCase().includes(q) ||
        d.brand?.toLowerCase().includes(q) ||
        d.fabric?.toLowerCase().includes(q) ||
        d.vendor_name?.toLowerCase().includes(q)
      );
    }
    // Vendor filter
    if (vendorFilter) list = list.filter(d => d.vendor_id === vendorFilter);
    // Customer filter
    if (custFilter) list = list.filter(d => d.customer_breakdown.some(cb => cb.customer_id === custFilter));
    const dir = sortDir === 'asc' ? 1 : -1;
    const sorted = [...list].sort((a, b) => {
      if (sortKey === 'qty') return (a.qty_total - b.qty_total) * dir;
      if (sortKey === 'customers') return (a.customer_count - b.customer_count) * dir;
      return a.design_code.localeCompare(b.design_code, undefined, { numeric: true }) * dir;
    });
    return sorted;
  }, [report, searchTerm, vendorFilter, custFilter, sortKey, sortDir]);

  const toggle = (dc) => {
    const next = new Set(expanded);
    if (next.has(dc)) next.delete(dc); else next.add(dc);
    setExpanded(next);
  };

  const setSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'design' ? 'asc' : 'desc'); }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ArrowUpDown className="w-3 h-3 inline ml-1 opacity-50" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 inline ml-1" /> : <ArrowDown className="w-3 h-3 inline ml-1" />;
  };

  const downloadExport = async (fmt) => {
    setExporting(fmt);
    try {
      const body = eventIds?.length ? { event_ids: eventIds } : eventId ? { event_id: eventId } : { order_ids: selectedIds };
      body.qty_mode = qtyMode;
      if (vendorFilter) body.vendor_filter = vendorFilter;
      if (custFilter) body.customer_filter = custFilter;
      if (searchTerm) body.search_filter = searchTerm;
      const res = await api.post(`/sales-orders/consolidated-report/export?fmt=${fmt}`,
        body, { responseType: 'blob', timeout: 180000 }); // 3 minute timeout for large exports
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const suffix = eventName ? `-${eventName.replace(/\s+/g, '_')}` : '';
      a.download = `so-consolidated-report${suffix}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(`Failed to export ${fmt.toUpperCase()}`);
    } finally { setExporting(null); }
  };

  const drillColSpan = 9 + (report?.active_sizes.length || 0);

  // Design thumbnail — click to enlarge.
  const Thumb = ({ src }) => (
    src
      ? <img src={`${API_BASE}${src}`} alt="design" onClick={() => setZoomImg(`${API_BASE}${src}`)}
          className="w-12 h-12 object-cover rounded border cursor-zoom-in hover:ring-2 hover:ring-emerald-400 transition" data-testid="report-thumb" />
      : <div className="w-12 h-12 rounded border bg-gray-100 flex items-center justify-center text-[8px] text-gray-400 text-center leading-tight">No image</div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="consolidated-report-dialog">
        <DialogHeader>
          <DialogTitle>
            Consolidated Sales Order Report
            {(eventName || report?.event_name) && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-sm font-medium bg-pink-100 text-pink-800" data-testid="report-event-tag">
                Event: {eventName || report.event_name}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Eligible orders: confirmed + partial-fulfilled + fulfilled. Cancelled / closed orders are excluded.
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="text-sm text-gray-500 py-12 text-center">Building report…</p>}

        {!loading && report && (
          <>
            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="report-summary">
              <div><div className="text-2xl font-bold text-emerald-700">{report.summary.order_count}</div><div className="text-xs text-gray-600">Orders</div></div>
              <div><div className="text-2xl font-bold text-emerald-700">{report.summary.customer_count}</div><div className="text-xs text-gray-600">Customers</div></div>
              <div><div className="text-2xl font-bold text-emerald-700">{report.summary.design_count}</div><div className="text-xs text-gray-600">Designs</div></div>
              <div><div className="text-2xl font-bold text-emerald-700">{report.summary.grand_qty.toLocaleString('en-IN')}</div><div className="text-xs text-gray-600">Total Qty</div></div>
              <div><div className="text-2xl font-bold text-emerald-700">₹{report.summary.grand_amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div><div className="text-xs text-gray-600">Total Value</div></div>
            </div>

            {/* Qty Mode Toggle */}
            <div className="flex items-center gap-2 pt-2">
              <span className="text-xs text-gray-500 font-medium">Showing:</span>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                {[
                  { key: 'all', label: 'All Qty', desc: 'Total ordered' },
                  { key: 'pending', label: 'Pending', desc: 'Yet to dispatch' },
                  { key: 'dispatched', label: 'Dispatched', desc: 'Already shipped' },
                ].map(m => (
                  <button key={m.key}
                    onClick={() => { setQtyMode(m.key); loadReport(m.key); }}
                    title={m.desc}
                    disabled={loading}
                    data-testid={`qty-mode-${m.key}`}
                    className={`px-3 py-1 text-xs rounded-md font-semibold transition-colors ${
                      qtyMode === m.key
                        ? 'bg-white shadow-sm text-emerald-700 border border-emerald-200'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >{m.label}</button>
                ))}
              </div>
            </div>

            {/* Toolbar: tabs + filters + export */}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button onClick={() => setTab('grid')} data-testid="report-tab-grid"
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${tab === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>Size Grid</button>
                <button onClick={() => setTab('customers')} data-testid="report-tab-customers"
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${tab === 'customers' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                  <Users className="w-3.5 h-3.5 inline mr-1" />Design-wise Customers</button>
              </div>

              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadExport('xlsx')} disabled={exporting !== null} data-testid="report-export-xlsx">
                  <FileSpreadsheet className="w-4 h-4 mr-1.5" /> {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadExport('pdf')} disabled={exporting !== null} data-testid="report-export-pdf">
                  <FileText className="w-4 h-4 mr-1.5" /> {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
                </Button>
              </div>
            </div>

            {/* Search + Filters row */}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <div className="relative flex-1 min-w-[180px] max-w-[280px]">
                <Search className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" />
                <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search design, brand, fabric, vendor..."
                  className="h-8 pl-8 text-sm" data-testid="report-search" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Vendor:</span>
                <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
                  className="h-8 rounded-md border border-input bg-background px-2 text-sm max-w-[180px]" data-testid="report-vendor-filter">
                  <option value="">All vendors ({allVendors.length})</option>
                  {allVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Customer:</span>
                <select value={custFilter} onChange={e => setCustFilter(e.target.value)}
                  className="h-8 rounded-md border border-input bg-background px-2 text-sm max-w-[180px]" data-testid="report-customer-filter">
                  <option value="">All customers ({allCustomers.length})</option>
                  {allCustomers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {(searchTerm || vendorFilter || custFilter) && (
                <button onClick={() => { setSearchTerm(''); setVendorFilter(''); setCustFilter(''); }}
                  className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1" data-testid="report-clear-filters">
                  <X className="w-3.5 h-3.5" /> Clear
                </button>
              )}
              <span className="text-xs text-gray-400 ml-auto">{visibleDesigns.length} / {report.designs.length} designs</span>
            </div>

            {(custFilter || vendorFilter) && (
              <div className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded px-3 py-1.5" data-testid="report-filter-banner">
                {custFilter && <>Filtered to customer <strong>{allCustomers.find(c => c.id === custFilter)?.name}</strong></>}
                {custFilter && vendorFilter && ' · '}
                {vendorFilter && <>Vendor: <strong>{allVendors.find(v => v.id === vendorFilter)?.name}</strong></>}
                {' — '}{visibleDesigns.length} design(s). Export will include filtered designs only.
              </div>
            )}

            {/* ---------- SIZE GRID TAB ---------- */}
            {tab === 'grid' && (
              <div className="overflow-auto flex-1 border rounded-lg" data-testid="report-grid">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-[#1E1E23] text-white sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left w-8"></th>
                      <th className="px-2 py-2 text-center w-10">#</th>
                      <th className="px-2 py-2 text-center w-16">Image</th>
                      <th className="px-2 py-2 text-left cursor-pointer select-none" onClick={() => setSort('design')} data-testid="report-sort-design">Design<SortIcon k="design" /></th>
                      <th className="px-2 py-2 text-left">Brand</th>
                      <th className="px-2 py-2 text-left">Colour</th>
                      {report.active_sizes.map(s => <th key={s} className="px-2 py-2 text-center">{s}</th>)}
                      <th className="px-2 py-2 text-right border-r text-emerald-700">Clr Qty</th>
                      <th className="px-2 py-2 text-right cursor-pointer select-none" onClick={() => setSort('qty')} data-testid="report-sort-qty">Qty<SortIcon k="qty" /></th>
                      <th className="px-2 py-2 text-right cursor-pointer select-none" onClick={() => setSort('customers')} data-testid="report-sort-customers">Cust.<SortIcon k="customers" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDesigns.map((d, di) => {
                      const isExpanded = expanded.has(d.design_code);
                      const colorCount = d.rows.length;
                      return (
                        <React.Fragment key={d.design_code}>
                          {d.rows.map((r, ri) => (
                            <tr key={`${d.design_code}-${r.color}`} className="border-b hover:bg-emerald-50/40 transition-colors">
                              {ri === 0 && (
                                <td rowSpan={colorCount} className="px-2 py-2 border-r align-top text-center">
                                  <button onClick={() => toggle(d.design_code)} className="text-gray-500 hover:text-emerald-600" data-testid={`design-toggle-${d.design_code}`}>
                                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                  </button>
                                </td>
                              )}
                              {ri === 0 && <td rowSpan={colorCount} className="px-2 py-2 text-center text-gray-400 border-r align-top font-mono">{di + 1}</td>}
                              {ri === 0 && <td rowSpan={colorCount} className="px-2 py-2 border-r align-top text-center">{<Thumb src={d.image} />}</td>}
                              {ri === 0 && <td rowSpan={colorCount} className="px-2 py-2 font-semibold border-r align-top">{d.design_code}</td>}
                              {ri === 0 && <td rowSpan={colorCount} className="px-2 py-2 text-gray-600 border-r align-top text-xs">{d.brand}</td>}
                              <td className="px-2 py-2 border-r">{r.color}</td>
                              {report.active_sizes.map(s => (
                                <td key={s} className="px-2 py-2 text-center border-r">{r.by_size[s] || <span className="text-gray-300">·</span>}</td>
                              ))}
                              <td className="px-2 py-2 text-right font-semibold text-emerald-700 border-r">{Object.values(r.by_size).reduce((a, v) => a + (v || 0), 0)}</td>
                              {ri === 0 && <td rowSpan={colorCount} className="px-2 py-2 text-right font-bold border-r align-top">{d.qty_total}</td>}
                              {ri === 0 && <td rowSpan={colorCount} className="px-2 py-2 text-right text-blue-700 align-top font-medium">{d.customer_count}</td>}
                            </tr>
                          ))}
                          {isExpanded && (
                            <tr className="bg-blue-50/40">
                              <td colSpan={drillColSpan} className="p-3">
                                <div className="text-xs font-semibold text-blue-800 mb-1.5">Customer-wise breakdown for {d.design_code}</div>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-gray-500">
                                      <th className="px-2 py-1 text-left w-10">#</th>
                                      <th className="px-2 py-1 text-left">Customer</th>
                                      <th className="px-2 py-1 text-left">City</th>
                                      <th className="px-2 py-1 text-left">State</th>
                                      <th className="px-2 py-1 text-right">Qty</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {d.customer_breakdown.map((cb, idx) => (
                                      <tr key={cb.customer_id} className={`border-t border-blue-200/40 ${custFilter === cb.customer_id ? 'bg-amber-100' : ''}`}>
                                        <td className="px-2 py-1 text-gray-400">{idx + 1}</td>
                                        <td className="px-2 py-1 font-medium">{cb.customer_name}</td>
                                        <td className="px-2 py-1 text-gray-600">{cb.city}</td>
                                        <td className="px-2 py-1 text-gray-600">{cb.state}</td>
                                        <td className="px-2 py-1 text-right font-semibold">{cb.qty}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {visibleDesigns.length === 0 && (
                      <tr><td colSpan={drillColSpan} className="px-2 py-8 text-center text-gray-400">No eligible items.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* ---------- DESIGN-WISE CUSTOMERS TAB ---------- */}
            {tab === 'customers' && (
              <div className="overflow-auto flex-1 border rounded-lg" data-testid="report-customers-tab">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-[#1E1E23] text-white sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-center w-10">#</th>
                      <th className="px-3 py-2 text-center w-16">Image</th>
                      <th className="px-3 py-2 text-left cursor-pointer select-none" onClick={() => setSort('design')}>Design<SortIcon k="design" /></th>
                      <th className="px-3 py-2 text-left">Brand</th>
                      <th className="px-3 py-2 text-left">Customers (qty)</th>
                      <th className="px-3 py-2 text-right cursor-pointer select-none" onClick={() => setSort('customers')}>Cust.<SortIcon k="customers" /></th>
                      <th className="px-3 py-2 text-right cursor-pointer select-none" onClick={() => setSort('qty')}>Total Qty<SortIcon k="qty" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDesigns.map((d, di) => {
                      const breakdown = custFilter ? d.customer_breakdown.filter(cb => cb.customer_id === custFilter) : d.customer_breakdown;
                      return (
                        <tr key={d.design_code} className="border-b hover:bg-emerald-50/40 align-top" data-testid={`report-cust-row-${d.design_code}`}>
                          <td className="px-3 py-2 text-center text-gray-400 font-mono">{di + 1}</td>
                          <td className="px-3 py-2 text-center"><Thumb src={d.image} /></td>
                          <td className="px-3 py-2 font-semibold">{d.design_code}</td>
                          <td className="px-3 py-2 text-gray-600 text-xs">{d.brand}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1.5">
                              {breakdown.map(cb => (
                                <span key={cb.customer_id} data-testid={`report-cust-chip-${cb.customer_id}-${d.design_code}`}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${custFilter === cb.customer_id ? 'bg-amber-100 border-amber-300 text-amber-800 font-semibold' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                                  {cb.customer_name}{cb.city ? <span className="text-gray-400">· {cb.city}</span> : null}
                                  <strong className="text-emerald-700">{cb.qty}</strong>
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right text-blue-700 font-medium">{d.customer_count}</td>
                          <td className="px-3 py-2 text-right font-bold">{custFilter ? breakdown.reduce((s, cb) => s + cb.qty, 0) : d.qty_total}</td>
                        </tr>
                      );
                    })}
                    {visibleDesigns.length === 0 && (
                      <tr><td colSpan={7} className="px-2 py-8 text-center text-gray-400">No eligible items.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-[11px] text-gray-400 px-1 pt-1">
              {tab === 'grid'
                ? 'Click a column header to sort. Click the chevron next to any design to see which customers ordered it.'
                : 'Each design lists the customers who ordered it and their quantities. Use the Customer filter to focus on one buyer.'}
            </p>
          </>
        )}

        {!loading && !report && <p className="text-sm text-red-500 py-12 text-center">Could not load report.</p>}

        {zoomImg && (
          <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-6" onClick={() => setZoomImg(null)} data-testid="report-image-zoom">
            <img src={zoomImg} alt="design enlarged" className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
            <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setZoomImg(null)} data-testid="report-image-zoom-close">
              <X className="w-8 h-8" />
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConsolidatedReportDialog;
